/**
 * @fileoverview Module for `src/admin/ReassignLetters.js`.
 */

import React, { Component } from 'react';
import propTypes from 'prop-types';
import queryString from 'query-string';
import axios from 'axios';
import logger from '../lib/logger';
import Footer from '../page/Footer';
import Header from '../page/Header';
import i18n from '../locales/strings.json';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

/**
 * Admin form for reassigning adopted letters between users.
 */
class UserPicker extends Component {
  /**
   * Initializes autocomplete state, adopter selections, and UI flags
   * for the letter reassignment form.
   *
   * @param {Object} props - Component props.
   * @param {Object} props.auth - Auth client for obtaining access tokens.
   * @param {string} [props.locale='en'] - Locale code for i18n.
   */
  constructor(props) {
    super(props);
    this.state = {
      auth: props.auth,
      locale: props.locale || 'en',
      toAdopters: [],
      fromAdopters: [],
      toAdopter: '',
      fromAdopter: '',
      reassigning: false,
      successMessage: '',
      errorMessage: '',
      fromInput: '',
      toInput: '',
      includeKits: false,
      sameAddress: false,
    };
  }

  static get propTypes() {
    return {
      auth: propTypes.object,
      locale: propTypes.string,
      showHeader: propTypes.bool,
      showFooter: propTypes.bool,
    };
  }

  /**
   * Loads the initial set of adopter options for both pickers.
   *
   * @returns {Promise<void>} Resolves after users are loaded.
   */
  componentDidMount() {
    return this.getUsers();
  }

  /**
   * Fetches adopter options for either picker, optionally filtered by input.
   *
   * @param {string|null} [inputValue=null]
   * @param {'to'|'from'|null} [autoCompleteType=null]
   * @returns {Promise<void>} Resolves after picker options are updated.
   */
  getUsers = async (inputValue = null, autoCompleteType = null) => {
    const filter = { 'email:isNotNull': null, qualState: ['qualified', 'super_qualified', 'pre_qualified'] };
    if (inputValue) {
      filter['auth0Id:contains'] = inputValue;
    }
    const queryParams = {
      filter: JSON.stringify(filter),
      range: JSON.stringify([0, 9]),
      sort: JSON.stringify(['auth0Id', 'ASC']),
    };

    try {
      const accessToken = await this.props.auth.getAccessToken();
      const res = await axios({
        method: 'GET',
        headers: {
          Authorization: 'Bearer '.concat(accessToken),
        },
        url: `${ process.env.REACT_APP_API_URL }/v1/users?${ queryString.stringify(queryParams) }`,
      });
      if (!autoCompleteType) {
        this.setState({
          toAdopters: res.data,
          fromAdopters: res.data,
        });
      } else if (autoCompleteType === 'to') {
        this.setState({
          toAdopters: res.data,
        });
      } else if (autoCompleteType === 'from') {
        this.setState({
          fromAdopters: res.data,
        });
      }
    } catch (err) {
      logger.error(err);
      this.setState({
        errorMessage: err.message,
      });
    }
  };

  /**
   * Handles selection of the "to" (destination) adopter, preventing
   * selection of the same user in both pickers.
   *
   * @param {Event} event - The change event.
   * @param {Object|null} value - The selected adopter option.
   */
  changeToHandler = (event, value) => {
    event.preventDefault();
    if (value) {
      if (this.state.fromAdopter && this.state.fromAdopter.auth0Id === value.auth0Id) {
        this.setState({ errorMessage: i18n[this.state.locale]['reassign.letters.error.sameaccount'] });
      } else {
        this.setState({ toAdopter: { auth0Id: value.auth0Id, email: value.email }, errorMessage: '' });
      }
    }
  };

  /**
   * Handles selection of the "from" (source) adopter, preventing
   * selection of the same user in both pickers.
   *
   * @param {Event} event - The change event.
   * @param {Object|null} value - The selected adopter option.
   */
  changeFromHandler = (event, value) => {
    event.preventDefault();
    if (value) {
      if (this.state.toAdopter && this.state.toAdopter.auth0Id === value.auth0Id) {
        this.setState({ errorMessage: i18n[this.state.locale]['reassign.letters.error.sameaccount'] });
      } else {
        this.setState({ fromAdopter: { auth0Id: value.auth0Id, email: value.email }, errorMessage: '' });
      }
    }
  };

  /**
   * Toggles the "include letter kits" checkbox state.
   */
  changeIncludeKitsHandler = () => {
    this.setState({ includeKits: !this.state.includeKits });
  };

  /**
   * Toggles the "same mailing address" confirmation checkbox state.
   */
  changeSameAddressHandler = () => {
    this.setState({ sameAddress: !this.state.sameAddress });
  };

  /**
   * Executes the letter reassignment request.
   *
   * @returns {Promise<void>} Resolves after reassignment completes.
   */
  reassign = async () => {
    try {
      const accessToken = await this.props.auth.getAccessToken();
      const headers = {
        Authorization: 'Bearer '.concat(accessToken),
      };
      await axios.patch(`${ process.env.REACT_APP_API_URL }/v2/users/${ this.state.fromAdopter.auth0Id }/letters`, {
        update: {
          adopterId: this.state.toAdopter.auth0Id,
          includeKits: this.state.includeKits,
        },
      }, {
        headers,
      });
      this.setState({
        reassigning: false,
        successMessage: i18n[this.state.locale]['reassign.letters.success'].replace('{{toAdopter}}', this.state.toAdopter.auth0Id).replace('{{fromAdopter}}', this.state.fromAdopter.auth0Id),
      });
    } catch (err) {
      logger.error(err);
      this.setState({ reassigning: false, errorMessage: err.message });
    }
  };

  /**
   * Begins confirmation mode before reassignment is committed.
   *
   * @param {Event} event
   * @returns {void}
   */
  submitHandler = (event) => {
    event.preventDefault();
    this.setState({ reassigning: true });
  };

  /**
   * Cancels reassignment confirmation mode.
   *
   * @returns {void}
   */
  cancelReassign = () => {
    this.setState({ reassigning: false });
  };

  /**
   * Handles input changes in the "to" autocomplete, updating the
   * search text and refreshing the adopter options.
   *
   * @param {Event} event - The input change event.
   * @param {string} value - The current input text.
   */
  toSearch = (event, value) => {
    this.setState({ toInput: value });
    this.getUsers(value, 'to');
  };

  /**
   * Handles input changes in the "from" autocomplete, updating the
   * search text and refreshing the adopter options.
   *
   * @param {Event} event - The input change event.
   * @param {string} value - The current input text.
   */
  fromSearch = (event, value) => {
    this.setState({ fromInput: value });
    this.getUsers(value, 'from');
  };

  /**
   * Renders the two-column adopter picker form with "from" and "to"
   * autocompletes, checkbox options, confirmation/cancel controls,
   * and success/error alerts.
   *
   * @returns {JSX.Element} The letter reassignment form.
   */
  render() {
    return (
      <React.Fragment>
        <h4 className='mt-2 text-center'>{ i18n[this.state.locale]['reassign.letters'] }</h4>
        <div className='mt-4'>
          <form onSubmit={ this.submitHandler }>
            <div className='row justify-content-center'>
              <div className='mb-3 from-column d-inline-block pe-5'>
                <Autocomplete
                  freeSolo
                  getOptionDisabled={ (option) => this.state.toAdopter && option.auth0Id === this.state.fromAdopter.auth0Id }
                  getOptionLabel={ (option) => option && option.auth0Id }
                  id='to-user-select'
                  input={ this.state.fromInput }
                  onChange={ this.changeFromHandler }
                  onInputChange={ this.fromSearch }
                  options={ this.state.fromAdopters }
                  renderInput={ (params) => <TextField { ...params } disabled={ this.state.reassigning } id='select-original-adopter' label={ i18n[this.state.locale]['reassign.letters.original.adopter'] } required variant='outlined' /> }
                  style={{ width: 400 }}
                  value={ this.state.fromAdopter }
                />
                { this.state.fromAdopter.auth0Id
                  && (
                    <div className='adopter-link'>
                      <a
                        href={ `/admin#/user/${ this.state.fromAdopter.auth0Id }` }
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        { i18n[this.state.locale]['reassign.letters.from.adopter.link'] }
                      </a>
                    </div>
                  ) }
              </div>
              <div className='mb-3 to-column d-inline-block pe-5'>
                <Autocomplete
                  freeSolo
                  getOptionDisabled={ (option) => this.state.fromAdopter && option.auth0Id === this.state.fromAdopter.auth0Id }
                  getOptionLabel={ (option) => option && option.auth0Id }
                  id='to-user-select'
                  input={ this.state.toInput }
                  onChange={ this.changeToHandler }
                  onInputChange={ this.toSearch }
                  options={ this.state.toAdopters }
                  renderInput={ (params) => <TextField { ...params } disabled={ this.state.reassigning } id='select-new-adopter' label={ i18n[this.state.locale]['reassign.letters.new.adopter'] } required variant='outlined' /> }
                  style={{ width: 400 }}
                  value={ this.state.toAdopter }
                />
                { this.state.toAdopter.auth0Id
                  && (
                    <div className='adopter-link'>
                      <a
                        href={ `/admin#/user/${ this.state.toAdopter.auth0Id }` }
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        { i18n[this.state.locale]['reassign.letters.new.adopter.link'] }
                      </a>
                    </div>
                  ) }
              </div>
            </div>
            <div className='justify-content-left'>
              <input
                checked={ this.state.includeKits }
                className='ms-3 mb-3'
                id='includeKits'
                onChange={ this.changeIncludeKitsHandler }
                style={{
                  display: 'inline-block',
                }}
                type='checkbox'
              />
              <label className='ms-2' htmlFor='includeKits'>
                Also Reassign Letter Kits
              </label>
            </div>
            <div className='justify-content-left' hidden={ !this.state.includeKits }>
              <input
                checked={ this.state.sameAddress }
                className='ms-3 mb-3'
                id='sameAddress'
                onChange={ this.changeSameAddressHandler }
                style={{
                  display: 'inline-block',
                }}
                type='checkbox'
              />
              <label className='ms-2' htmlFor='sameAddress'>
                New adopter has the same mailing address as the old adopter
              </label>
            </div>
            <div className='row justify-content-center'>
              <input
                className='btn btn-primary justify-content-center mt-3 ps-5 pe-5'
                disabled={ this.state.reassigning || (this.state.includeKits && !this.state.sameAddress) }
                type='submit'
                value={ (this.state.includeKits && !this.state.sameAddress) ? 'Please confirm the addresses are the same' : i18n[this.state.locale].submit }
              />
            </div>
            <div className={ ['text-center', 'mt-2', 'mb-2', this.state.reassigning ? undefined : 'hidden'].join(' ') }>
              { this.state.reassigning
                && (
                  <div>
                    <p className='me-4 ms-4 small'>
                      { i18n[this.state.locale]['reassign.letters.warning'].replace('{{toAdopter}}', `${ this.state.toAdopter.auth0Id } (${ this.state.toAdopter.email })`).replace('{{fromAdopter}}', `${ this.state.fromAdopter.auth0Id } (${ this.state.fromAdopter.email })`) }
                    </p>
                    <button
                      className='btn btn-info btn-small me-2'
                      onClick={ this.cancelReassign }
                      type='cancel'
                    >
                      { i18n[this.state.locale].cancel }
                    </button>

                    <button
                      className='btn btn-danger btn-small ms-2'
                      onClick={ this.reassign }
                      type='submit'
                    >
                      { i18n[this.state.locale].confirm }
                    </button>
                  </div>
                ) }
            </div>
          </form>
        </div>
        <div className={ ['alert', 'alert-info', 'mt-2', this.state.successMessage ? '' : 'hidden'].join(' ') } role='alert'>
          { this.state.successMessage }
        </div>
        <div className={ ['alert', 'alert-danger', 'mt-2', this.state.errorMessage ? '' : 'hidden'].join(' ') } role='alert'>
          { this.state.errorMessage }
        </div>
      </React.Fragment>
    );
  }
}

/**
 * Wrapper page for the letter reassignment tool.
 */
class ReassignLetters extends Component {
  static get propTypes() {
    return {
      auth: propTypes.object,
      showHeader: propTypes.bool,
      showFooter: propTypes.bool,
    };
  }

  /**
   * Renders the reassign letters page with optional header, the UserPicker
   * form, and optional footer.
   *
   * @returns {JSX.Element} The reassign letters page layout.
   */
  render() {
    return (
      <div className='position-relative'>
        { this.props.showHeader ? <Header /> : null }
        <UserPicker auth={ this.props.auth } />
        { this.props.showFooter ? <Footer /> : null }
      </div>
    );
  }
}

export default ReassignLetters;
